import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { INiboAccountantFileDataDTO } from 'src/modules/nibo-docs/models/interfaces/nibo-accountant-file-data.interface';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { CacheBuster, Cacheable } from 'ts-cacheable';
import { Calcs } from '../utilities/calculations';
import { IFileDocument, IFileDocumentData, IFileMetadata } from '../models/interfaces/files.interface';
import { FileDocumentData } from '../models/file.model';
import { FilesHelper } from '../helpers/files.helper';

const cacheBuster$ = new Subject<void>();

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  constructor(private _http: HttpClient) { }

  /**
   * Pega informações básicas de um arquivo.
   * Não possui cache para evitar que os classificadores classifiquem o mesmo arquivo.
   */
  @Cacheable({
    maxAge: 1000 * 60 * 15,
    maxCacheCount: 25,
    cacheBusterObserver: cacheBuster$,
  })
  public getFile(fileId: string): Observable<IFileDocument> {
    return this._http.get<IFileDocument>(`${environment.filesApiUrl}/files/${fileId}/details`).pipe(
      map((file) => {
        file.fileExtension = file.name.split('.')[file.name.split('.').length - 1].toLowerCase();
        file.fileURI = environment.filesApiUrl + '/download/' + file.id + '?fileName=' + file.name;
        return FilesHelper.normalizeFileType(file);
      })
    );
  }

  public viewFile(fileUri: string): void {
    window.open(fileUri, 'location=0,menubar=0');
  }

  @CacheBuster({ cacheBusterNotifier: cacheBuster$ })
  public updateFile(fileId: string, fileData: { [id: string]: any }): Observable<void> {
    return this._http.put<void>(`${environment.accountantApiUrl}/classification/documents/${fileId}`, fileData);
  }

  /**
   * Pega metadados de um arquivo.
   */
  @Cacheable({ maxAge: 1000 * 60 * 15, maxCacheCount: 25 })
  public getFileMetadata(fileId: string): Observable<IFileMetadata> {
    return this._http.get<IFileMetadata>(`${environment.filesApiUrl}/files/${fileId}/metadata`);
  }

  /**
   * @deprecated Used only with NiboDocs integration
   */
  public getFilesFromOrganization(accountantId: string, organizationId: string): Observable<IFileDocumentData[]> {
    return this._http
      .get<INiboAccountantFileDataDTO[]>(
        `${environment.accountantApiUrl}/classification/pendingAccountants/${accountantId}/${organizationId}/documents`
      )
      .pipe(
        map((documents) => {
          return this._orderFilesByName(
            documents.map<IFileDocumentData>(
              (document) =>
                new FileDocumentData(
                  {
                    contentType: document.fileExtension === 'pdf' ? 'application/pdf' : 'image/jpeg',
                    fileExtension: document.fileExtension,
                    fileType: document.fileType,
                    fileURI: document.fileURI,
                    id: document.fileId,
                    name: document.fileName,
                    size: 0,
                  },
                  {
                    organizationId: document.organizationId,
                    organizationName: document.organizationName,
                    accountantId: document.accountantId,
                    accountantName: document.accountantName,
                  }
                )
            )
          );
        })
      );
  }

  public uploadFile(formData: FormData): Observable<{ id: string; name: string }> {
    return this._http.post<{ id: string; name: string }>(`${environment.filesApiUrl}/upload`, formData);
  }

  public getFileContent(fileId: string): Observable<string> {
    return this._http.get(`${environment.filesApiUrl}/download/${fileId}`, { responseType: 'text' });
  }

  public getTags(searchString: string): Observable<string[]> {
    return this._http
      .get<{ tags: string[] }>(`${environment.filesApiUrl}/classificationteam/tags?containsname=${searchString}`)
      .pipe(map((result) => result.tags));
  }

  public getFileTags(fileId: string): Observable<string[]> {
    return this._http
      .get<{ tags: string[] }>(`${environment.filesApiUrl}/classificationteam/files/${fileId}`)
      .pipe(map((result) => result.tags));
  }

  public updateFileTags(fileId: string, tags: string[]): Observable<void> {
    return this._http.post<void>(`${environment.filesApiUrl}/classificationteam/tags`, { fileId, tags });
  }

  private _orderFilesByName(files: IFileDocumentData[]): IFileDocumentData[] {
    return this._sortFilesByNameUsingLevensteinDistance(files.sort((a, b) => (a.name > b.name ? 1 : -1)));
  }

  private _sortFilesByNameUsingLevensteinDistance(files: IFileDocumentData[]): IFileDocumentData[] {
    return files.sort((a, b) => {
      const distance = Calcs.levenshteinDistanceStrings(a.name, b.name);

      if (distance === 0) {
        return 0;
      } else {
        return distance < 3 ? 1 : -1;
      }
    });
  }
}
